/* TASK-234 (issue #352, EPIC-021 #227) — pure helpers behind the admin
   "Credits" tab (AdminUsers → user detail → Credits, AdminUserCredits.jsx).

   The ledger (stripe-worker credit-ledger.ts) is kept in integer CENTS and
   1 credit = 1 EUR, so 100 cents = 1 credit. The admin types credits; the
   API takes cents. Validation error values are KEYS under the
   `adminCredits.errors` i18n block, not messages, so the screen can render
   them in the admin's language. No React, no DOM — plain vitest covers it. */

export const CREDIT_ACCOUNTS = ['main', 'bonus'];

// credit-ledger.ts CREDIT_KINDS, same order.
export const CREDIT_KINDS = [
  'topup', 'bonus', 'spend', 'convert_in', 'convert_out',
  'expire', 'refund', 'refund_out', 'revoke', 'admin_grant',
];

// credit-ledger.ts CreditGrantStatus.
export const GRANT_STATUSES = ['active', 'expired', 'consumed', 'lapsed'];

const CREDIT_KINDS_IN = new Set(['topup', 'bonus', 'convert_in', 'refund', 'admin_grant']);
const CREDIT_KINDS_OUT = new Set(['spend', 'convert_out', 'expire', 'refund_out', 'revoke']);

export function kindDirection(kind) {
  if (CREDIT_KINDS_IN.has(kind)) return 'credit';
  if (CREDIT_KINDS_OUT.has(kind)) return 'debit';
  return 'unknown';
}

export function centsToCredits(cents) {
  const n = Number(cents);
  return Number.isFinite(n) ? n / 100 : 0;
}

// What an admin types ("12.5", "12,5", 3) → integer cents, or null when it is
// not a clean non-negative money amount (sub-cent, exponent, sign, junk).
const CREDIT_INPUT = /^\d+(?:\.\d{1,2})?$/;

export function creditsToCents(input) {
  if (typeof input === 'number') {
    if (!Number.isFinite(input) || input < 0) return null;
    const cents = Math.round(input * 100);
    return Math.abs(input * 100 - cents) > 1e-6 ? null : cents;
  }
  if (typeof input !== 'string') return null;
  const normalized = input.trim().replace(',', '.');
  if (!CREDIT_INPUT.test(normalized)) return null;
  return Math.round(Number.parseFloat(normalized) * 100);
}

export function formatCredits(cents, { signed = false } = {}) {
  if (cents == null) return '—';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  const value = n / 100;
  const abs = Math.abs(value).toFixed(2);
  if (value < 0) return `-${abs}`;
  if (signed && value > 0) return `+${abs}`;
  return abs;
}

export function validateReason(reason) {
  if (typeof reason !== 'string') return null;
  const trimmed = reason.trim();
  return trimmed ? trimmed : null;
}

export function validateGrant({ organizationId, account, amount, reason } = {}) {
  const errors = {};
  const orgId = typeof organizationId === 'string' ? organizationId.trim() : '';
  if (!orgId) errors.organizationId = 'organizationRequired';
  if (!CREDIT_ACCOUNTS.includes(account)) errors.account = 'accountInvalid';
  const amountCents = creditsToCents(amount);
  if (amountCents === null) errors.amount = 'amountInvalid';
  else if (amountCents <= 0) errors.amount = 'amountPositive';
  const trimmedReason = validateReason(reason);
  if (!trimmedReason) errors.reason = 'reasonRequired';
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, value: { organizationId: orgId, account, amountCents, reason: trimmedReason } };
}

// `context.mainCents` (the live main balance, when known) lets the screen
// refuse a main revoke the API would 400 anyway ("never overdraws main").
export function validateRevoke(form = {}, context = {}) {
  const base = validateGrant(form);
  const errors = base.ok ? {} : { ...base.errors };
  const grantKey = typeof form.grantKey === 'string' ? form.grantKey.trim() : '';
  if (grantKey && form.account === 'main') errors.grantKey = 'grantKeyBonusOnly';
  const mainCents = context ? context.mainCents : undefined;
  if (base.ok && form.account === 'main' && Number.isFinite(mainCents) && base.value.amountCents > mainCents) {
    errors.amount = 'amountExceedsMain';
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  const value = { ...base.value };
  if (grantKey) value.grantKey = grantKey;
  return { ok: true, value };
}

// Query for GET /api/admin/credits/history: only set filters, values the API
// accepts ("all" and unknown values are dropped rather than 400-ing).
export function buildHistoryParams({ organizationId, account, kind, from, to, limit, offset } = {}) {
  const params = {};
  if (organizationId) params.organizationId = organizationId;
  if (CREDIT_ACCOUNTS.includes(account)) params.account = account;
  if (CREDIT_KINDS.includes(kind)) params.kind = kind;
  if (from) params.from = from;
  if (to) params.to = to;
  if (Number.isInteger(limit) && limit > 0) params.limit = String(limit);
  if (Number.isInteger(offset) && offset > 0) params.offset = String(offset);
  return params;
}
